import React, { useState, useEffect, useCallback } from 'react';
import { Users, UserPlus, Trash2, Loader2 } from 'lucide-react';
import { organizationApi } from 'api';

const formatExpiry = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const UsersTab = ({ onError, onSuccess }) => {
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [revokingId, setRevokingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, iRes] = await Promise.all([
        organizationApi.getMembers(),
        organizationApi.getInvitations(),
      ]);
      setMembers(mRes.data?.members || []);
      setInvitations(iRes.data?.invitations || []);
    } catch (err) {
      console.error(err);
      onError?.(err.response?.data?.error || 'Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  const displayName = (u) => {
    const parts = [u.first_name, u.last_name].filter(Boolean);
    if (parts.length) return parts.join(' ');
    return u.name || u.email;
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      onError?.('Ingresá un email');
      return;
    }
    setSubmitting(true);
    try {
      await organizationApi.createInvitation(trimmed);
      onSuccess?.('Invitación enviada');
      setEmail('');
      await load();
    } catch (err) {
      onError?.(err.response?.data?.error || 'No se pudo enviar la invitación');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (id) => {
    setRevokingId(id);
    try {
      await organizationApi.revokeInvitation(id);
      onSuccess?.('Invitación revocada');
      await load();
    } catch (err) {
      onError?.(err.response?.data?.error || 'No se pudo revocar');
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-2xl shadow-lg p-4 md:p-8">
      <div className="pb-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-500/40 flex items-center justify-center backdrop-blur-md">
            <Users className="w-5 h-5 text-white" aria-hidden />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">Usuarios</h3>
            <p className="text-white/70 text-sm">
              Invitá a tu equipo a colaborar en la misma organización.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-8 pt-6 md:pt-8">
        <section className="space-y-4">
          <h4 className="text-white/90 font-semibold text-sm uppercase tracking-wide">
            Invitar por email
          </h4>
          <form
            onSubmit={handleInvite}
            className="flex flex-col gap-3 sm:flex-row sm:items-end rounded-xl border border-white/10 bg-white/5 p-4 md:p-5"
          >
            <div className="flex-1 min-w-0">
              <label htmlFor="invite-email" className="sr-only">
                Email
              </label>
              <input
                id="invite-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="correo@ejemplo.com"
                autoComplete="email"
                className="w-full px-4 py-3 backdrop-blur-md bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-blue-400 focus:bg-white/15"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-sm font-medium shadow-lg transition-all disabled:opacity-50 shrink-0"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4" />
              )}
              Invitar
            </button>
          </form>
        </section>

        <section className="space-y-4 pt-2 border-t border-white/10">
          <h4 className="text-white/90 font-semibold text-sm uppercase tracking-wide">
            Miembros
          </h4>
          {loading ? (
            <div className="flex justify-center py-8 text-white/60">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : members.length === 0 ? (
            <p className="text-white/60 text-sm">No hay miembros para mostrar.</p>
          ) : (
            <ul className="divide-y divide-white/10 rounded-xl border border-white/10 overflow-hidden">
              {members.map((u) => (
                <li
                  key={u.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 px-4 py-3 bg-white/5"
                >
                  <span className="text-white font-medium">{displayName(u)}</span>
                  <span className="text-white/60 text-sm">{u.email}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="space-y-4 pt-2 border-t border-white/10">
          <h4 className="text-white/90 font-semibold text-sm uppercase tracking-wide">
            Invitaciones pendientes
          </h4>
          {!loading && invitations.length === 0 ? (
            <p className="text-white/60 text-sm">No hay invitaciones pendientes.</p>
          ) : !loading ? (
            <ul className="divide-y divide-white/10 rounded-xl border border-white/10 overflow-hidden">
              {invitations.map((inv) => (
                <li
                  key={inv.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 bg-white/5"
                >
                  <div>
                    <span className="text-white text-sm">{inv.email}</span>
                    <p className="text-white/50 text-xs mt-0.5">
                      Vence: {formatExpiry(inv.expires_at)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRevoke(inv.id)}
                    disabled={revokingId === inv.id}
                    className="inline-flex items-center gap-1.5 text-sm text-red-300 hover:text-red-200 border border-red-500/40 rounded-lg px-3 py-1.5 hover:bg-red-500/10 disabled:opacity-50 self-start sm:self-center"
                  >
                    {revokingId === inv.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                    Revocar
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      </div>
    </div>
  );
};

export default UsersTab;
